/**
 * Arabic for the live SAP connection: the settings panel on the finance
 * system screen, the notices that say whose read the screen is showing, and
 * the platform's answers when it tests or reads. Proposed; pending native
 * review by somebody who administers SAP in Arabic.
 *
 * SAP's own terms stay as finance and SAP staff in the Gulf use them, as in
 * finance-system.ts: رمز الشركة for the company code, عميل SAP for the
 * sap-client, and OAuth, URL and the service names in Latin script exactly as
 * SAP writes them — the person reading this will type them into SAP's own
 * screens.
 *
 * The secret is always "السر" here and never a gentler word: the panel's
 * point is that it is sent once and never shown again, and that has to read
 * as firmly in Arabic as it does in English.
 */
const ar: Record<string, string> = {
  /* ── whose read the screen shows ───────────────────────────────────── */
  "Read from the finance system.": "مقروءة من النظام المالي.",
  "Everything below is the journal file {name}, read on the platform by the same rules as a live read.":
    "كل ما يلي هو ملف القيود {name}، قرأته المنصة بالقواعد نفسها التي تقرأ بها القراءة المباشرة.",
  "Everything below is the platform's latest read of {system}, for {year} period {period}.":
    "كل ما يلي هو أحدث قراءة للمنصة من {system}، لسنة {year} الفترة {period}.",
  "Not read yet.": "لم تُقرأ بعد.",
  "The platform has not read the finance system for this site yet. Until it does, everything below is the read running against a sample — the mapping, the exclusions and the checks are real; the figures are not the airport's. Set up the connection at the foot of this page.":
    "لم تقرأ المنصة النظام المالي لهذا الموقع بعد. وإلى أن تفعل، فكل ما يلي هو القراءة تعمل على عيّنة — التصنيف والاستبعادات والتحققات حقيقية، أما الأرقام فليست أرقام المطار. اضبط الاتصال في أسفل هذه الصفحة.",

  /* ── the panel ─────────────────────────────────────────────────────── */
  "SAP connection": "الاتصال بـ SAP",
  "where the platform reads the journal from; the secret is sent once and never shown again":
    "من أين تقرأ المنصة دفتر القيود؛ يُرسَل السر مرة واحدة ولا يُعرض مرة أخرى أبدًا",
  "The demonstration keeps these settings for this session only and never reaches SAP. A journal file sent below is read here exactly as the platform reads it.":
    "يحتفظ العرض التوضيحي بهذه الإعدادات لهذه الجلسة فقط ولا يتصل بـ SAP. أما ملف القيود المرسل أدناه فيُقرأ هنا تمامًا كما تقرؤه المنصة.",
  "Setting up the connection is an administrator's. What the last read found is below for anybody who can see the finance system.":
    "ضبط الاتصال من صلاحيات مسؤول النظام. وما وجدته آخر قراءة معروض أدناه لكل من يستطيع رؤية النظام المالي.",
  "Name for this system": "اسم هذا النظام",
  "S/4HANA · production": "S/4HANA · الإنتاج",
  "SAP address": "عنوان SAP",
  "How the platform signs in": "كيف تسجّل المنصة الدخول",
  "OAuth 2.0 client credentials (S/4HANA Cloud)": "بيانات اعتماد العميل OAuth 2.0 ‏(S/4HANA Cloud)",
  "User and password (communication or technical user)": "مستخدم وكلمة مرور (مستخدم اتصال أو مستخدم تقني)",
  "Token URL": "عنوان الرمز (Token URL)",
  "Scope (if SAP asks for one)": "النطاق (إن طلبه SAP)",
  User: "المستخدم",
  "SAP client (on-premise)": "عميل SAP (للأنظمة المحلية)",
  "Company codes": "رموز الشركات",
  "Read on a schedule": "القراءة بجدول زمني",
  "only when asked": "عند الطلب فقط",
  "every hour": "كل ساعة",
  "every six hours": "كل ست ساعات",
  "once a day": "مرة في اليوم",
  "Service path": "مسار الخدمة",
  "Plan entity path (optional)": "مسار كيان الموازنة (اختياري)",
  "none — actuals only": "لا شيء — الفعلي فقط",
  "Password or client secret:": "كلمة المرور أو سر العميل:",
  "No secret is set.": "لم يُضبط أي سر.",
  "Set — read from the platform's environment as {name}.": "مضبوط — يُقرأ من بيئة المنصة باسم {name}.",
  "Named {name}, but the platform's environment does not hold it yet.": "مُسمّى {name}، لكن بيئة المنصة لا تحمله بعد.",
  "Set — entered once and kept sealed on the platform.": "مضبوط — أُدخل مرة واحدة ويُحفظ مختومًا على المنصة.",
  "Entered once, but this platform can no longer open it; enter it again or name an environment secret.":
    "أُدخل مرة واحدة، لكن هذه المنصة لم تعد قادرة على فتحه؛ أدخله مرة أخرى أو سمِّ سرًا من البيئة.",
  "keep it as it is": "إبقاؤه كما هو",
  "name a secret the platform holds": "تسمية سرّ تحمله المنصة",
  "enter it once": "إدخاله مرة واحدة",
  "Environment secret name": "اسم السر في البيئة",
  "This platform holds no key to keep a typed secret, so it would refuse one. Name an environment secret instead — your administrator adds it to the platform's Secret Manager.":
    "لا تحمل هذه المنصة مفتاحًا لحفظ سرّ مكتوب، ولذلك سترفضه. سمِّ سرًا من البيئة بدلًا من ذلك — يضيفه مسؤول النظام إلى مدير الأسرار في المنصة.",
  "Secret — sent once, never shown again": "السر — يُرسَل مرة واحدة ولا يُعرض مرة أخرى أبدًا",
  "Enabled — the schedule may read from SAP": "مُفعَّل — يجوز للجدول الزمني القراءة من SAP",
  "Save the connection": "حفظ الاتصال",
  "Testing…": "جارٍ الاختبار…",
  "Test the connection": "اختبار الاتصال",
  "Reading…": "جارٍ القراءة…",
  "Read now": "اقرأ الآن",
  "Saved. The secret, if one was sent, is not shown again.": "حُفظ. والسر، إن أُرسل، لن يُعرض مرة أخرى.",
  "Reading {year} period {period} from SAP. The report below updates when it finishes.":
    "جارٍ قراءة سنة {year} الفترة {period} من SAP. يتحدّث التقرير أدناه عند انتهائها.",
  "The journal file was read: {n}. It is now the read shown above.":
    "قُرئ ملف القيود: {n}. وهو الآن القراءة المعروضة أعلاه.",
  "The management pack was recorded: {n} placed by its rules.": "سُجّلت الحزمة الإدارية: {n} صُنّفت بقواعدها.",
  "Send a journal file (CSV in SAP's field names) or the monthly pack:":
    "أرسل ملف قيود (CSV بأسماء حقول SAP) أو الحزمة الشهرية:",
  "Journal or management pack file": "ملف القيود أو الحزمة الإدارية",

  /* ── the last read ─────────────────────────────────────────────────── */
  "The last read": "آخر قراءة",
  "The platform did not say how the last read went.": "لم تذكر المنصة كيف جرت آخر قراءة.",
  "Nothing has been read yet.": "لم يُقرأ شيء بعد.",
  "Reading since {when}.": "جارٍ القراءة منذ {when}.",
  "{source} on {when}: {n} read, {mapped} placed.": "{source} في {when}: قُرئ {n}، وصُنّف {mapped}.",
  "From the file {name}": "من الملف {name}",
  "From SAP": "من SAP",
  "Failed on {when}: {why}": "فشلت في {when}: {why}",
  "the read did not complete": "لم تكتمل القراءة",
  "Not counted, and why": "ما لم يُحتسب، ولماذا",
  "Accounts no rule covers: {list}.": "حسابات لا تشملها أي قاعدة: {list}.",
  "Every line was counted, and every account had a rule.": "احتُسب كل سطر، وكان لكل حساب قاعدة.",
  "Reads on its own every {n} minutes; the last successful read was {when}.":
    "تقرأ من تلقاء نفسها كل {n} دقيقة؛ وكانت آخر قراءة ناجحة في {when}.",

  /* ── what a failure means (domain/sapConnection.ts, and the platform's report) ── */
  "the connection is missing something it needs before it can call": "ينقص الاتصالَ شيء يحتاجه قبل أن يتصل",
  "the address is not one this deployment will call": "هذا العنوان ليس مما تتصل به هذه المنصة",
  "SAP refused the credentials (401): the user, password, client id or secret is wrong or expired":
    "رفض SAP بيانات الاعتماد (401): المستخدم أو كلمة المرور أو معرّف العميل أو السر خاطئ أو منتهي الصلاحية",
  "SAP accepted who we are and refused what we asked (403): the communication user lacks an authorisation":
    "قبل SAP هويتنا ورفض ما طلبناه (403): ينقص مستخدمَ الاتصال تفويضٌ",
  "SAP has no such service at that address (404): the service is not activated, or the path or sap-client is wrong":
    "لا توجد لدى SAP خدمة كهذه في ذلك العنوان (404): الخدمة غير مفعّلة، أو المسار أو عميل SAP خاطئ",
  "SAP could not be reached, after retrying": "تعذّر الوصول إلى SAP بعد إعادة المحاولة",
  "SAP answered with a server error, after retrying": "ردّ SAP بخطأ في الخادم بعد إعادة المحاولة",
  "SAP answered with something that is not the journal": "ردّ SAP بشيء ليس دفتر القيود",
  "the read is larger than one read is allowed to be; narrow it by period or company code":
    "القراءة أكبر مما يُسمح به لقراءة واحدة؛ ضيّقها بالفترة أو برمز الشركة",
  "the read was abandoned: the process running it stopped before it finished":
    "تُركت القراءة: توقفت العملية التي تشغّلها قبل أن تنتهي",
  "the platform failed while storing the read": "فشلت المنصة أثناء حفظ القراءة",

  /* ── the platform's answer to a test (finance/store.py test_connection) ── */
  "SAP answered and the journal is readable.": "ردّ SAP ودفتر القيود قابل للقراءة.",
  "SAP answered, but returned no journal line for that company code.":
    "ردّ SAP، لكنه لم يُرجع أي سطر قيد لرمز الشركة ذلك.",
  "The demonstration makes no call to SAP. On the platform this reaches SAP, signs in and reads one journal line.":
    "لا يتصل العرض التوضيحي بـ SAP. أما على المنصة فهذا يصل إلى SAP ويسجّل الدخول ويقرأ سطر قيد واحدًا.",
};
export default ar;
